'use client';

import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { analytics } from '@/lib/analytics';
import { TONE_CARD_SELECTOR, ToneSurface, ToneSurfaceContext } from './ToneSurface';

interface Props extends ToneSurface {
  /** The row's tones, in display order — supplies the impression count. */
  tones: { id: number | null }[];
  children: ReactNode;
}

// Names a row of tone cards and records its impressions. The cards inside read
// the name off the context this provides and emit their own play and
// click-through events, so all three signals share one vocabulary and one
// source of truth for which row they came from.
//
// Wraps a row rather than living inside ToneSection so that stays a server
// component: a server caller passes its rendered row through `children` and
// keeps it out of the client bundle.
export const ToneRowTracking = ({
  surface,
  properties,
  cardProperties,
  tones,
  children,
}: Props) => {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const hasFiredRef = useRef(false);
  // Read at fire time so a caller's inline object literal doesn't re-arm the
  // observer on every render.
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;

  // Content, not array identity: re-arms when the row actually changes, which a
  // client-side hop between two tone pages can do without remounting.
  const rowKey = tones.map((tone) => tone.id).join(',');
  const toneCount = tones.length;

  useEffect(() => {
    hasFiredRef.current = false;

    const row = rowRef.current;
    if (!row || !toneCount) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        if (hasFiredRef.current) return;
        hasFiredRef.current = true;
        // Measured, not `tones.length`: ToneCardRow's lg+ grid renders a capped
        // slice of the tones its carousel renders in full, so only the cards
        // this viewport actually laid out are a comparable CTR denominator.
        const rendered = Array.from(row.querySelectorAll(TONE_CARD_SELECTOR)).filter(
          (card) => card.getClientRects().length > 0
        ).length;
        analytics.capture('tone.card_row.viewed', {
          surface,
          count: rendered || toneCount,
          ...propertiesRef.current,
        });
      },
      // Bottom inset instead of a ratio: a row can be taller than the viewport
      // on mobile, where a ratio threshold would never fire.
      { rootMargin: '0px 0px -120px 0px' }
    );

    observer.observe(row);
    return () => observer.disconnect();
  }, [surface, rowKey, toneCount]);

  const value = useMemo(
    () => ({ surface, properties, cardProperties }),
    [surface, properties, cardProperties]
  );

  return (
    <ToneSurfaceContext.Provider value={value}>
      <div ref={rowRef}>{children}</div>
    </ToneSurfaceContext.Provider>
  );
};
